
// https://stackoverflow.com/questions/1419194/algorithm-for-nice-graph-labels-for-time-date-axis
// http://www.acooke.org/cute/AutoScalin0.html

// There's a nice algorithm for auto-scaling axes, called the "nice number
// algorithm", written by Paul Heckbert and published in "Graphics Gems"

import { sprintf } from 'sprintf-js';

/**
 * This lists fractional limits for different number bases.
 *
 * rfs: rounded fractional step size.
 * fs: fractional step size.
 *
 * Each of these map fractional 'f' to a number of steps.
 */
const LIM10 = {
  base: 10,
  rfs: [[1.5, 1], [3, 2], [7, 5]],
  fs: [1, 2, 5],
};

const LIM12 = {
  base: 12,
  rfs: [[1.5, 1], [3, 2], [8, 6]],
  fs: [1, 2, 6],
};

const LIM60 = {
  base: 60,
  rfs: [[1.5, 1], [20, 15], [40, 30]],
  fs: [1, 15, 40],
};

/**
 * Heckberts "nice numbers" algorithm for graph ranges, from
 * "Graphics Gems"
 *
 * @param lo: {float} from value.
 * @param hi: {float} to value
 * @param ntick: {int} number of ticks requested
 * @param limits: One of the structs above.
 * @return {{float}} The nice number.
 */
const heckbert_d = (lo, hi, ntick=5, limits=LIM10) => {

  let base = limits.base;
  let rfs = limits.rfs;
  let fs = limits.fs;

  let nicenum = (x, round) => {

    let step = Math.pow(
      base, Math.floor(Math.log(x) / Math.log(base)));
    let f = x / step;
    let nf = base;
    if (round) {
      for(let i = 0; i < rfs.length; i++) {
        let a = rfs[i][0];
        let b = rfs[i][1];
        if (f < a) {
          nf = b;
          break;
        }
      }
    } else {
      for (let i = 0; i < fs.length; i++) {
        let a = fs[i];
        if (f <= a) {
          nf = a;
          break;
        }
      }
    }
    return nf * step;
  };

  let delta = nicenum(hi - lo, false);
  return nicenum(delta / (ntick - 1), true);
};

/**
 * Given a range from {@param lo} to {@param hi}, {@param ntick}
 * ticks, and base set to {@param limits}, If {@param cash_fmt}
 * is {@code true}, then we contract values such as 1000000 => 1mm.
 * 1000 => 1k, etc.
 *
 * return [ {{label: string, value: float}} ]
 */
const heckbert = (lo, hi, ntick=5, limits=LIM10, cash_fmt=false) => {

  let result = [];

  let d = heckbert_d(lo, hi, ntick, limits);
  let graphlo = Math.floor(lo / d) * d;
  let graphhi = Math.ceil(hi / d) * d;
  let fmt = '%' + sprintf('.%df', Math.max(-Math.floor(Math.log10(d)), 0));
  let value_scale = 1;

  if (cash_fmt) {
    if (Math.log10(graphlo) >= 6) {
      fmt = '$%.2fmm';
      value_scale = 1e6;
    } else if (Math.log10(graphlo) >= 3) {
      fmt = '$%.2fk';
      value_scale = 1e3;
    } else {
      fmt = "$" + fmt;
    }
  }

  let value = graphlo;
  while (value < graphhi + 0.5*d) {
    result.push({
      label: sprintf(fmt, value/value_scale),
      value: value,
    });
    value += d;
  }
  return result;
};

export { heckbert, LIM10, LIM12, LIM60 };
